import { APP_INITIALIZER, ErrorHandler, NgModule } from '@angular/core';
import * as Sentry from '@sentry/angular';
import { Router } from '@angular/router';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { MatIconModule } from '@angular/material/icon';
import { MatFormFieldModule } from '@angular/material/form-field';
import { ReactiveFormsModule } from '@angular/forms';
import { MatSelectModule } from '@angular/material/select';
import { MatMenuModule } from '@angular/material/menu';
import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { MatDialogModule } from '@angular/material/dialog';

import { AppComponent } from './app.component';
import { ErrorDialogComponent } from './components/general/error-dialog/error-dialog.component';
import { LoginComponent } from './pages/general/login/login.component';
import { HomeComponent } from './pages/learner/home/home.component';
import { ResultsGuidedComponent } from './pages/learner/results/results-guided/results-guided.component';
import { TeamAdminHomeComponent } from './pages/team-admin/team-admin-home/team-admin-home.component';
import { AppRoutingModule } from './app-routing.module';
import { NavbarComponent } from './components/layouts/navbar/navbar.component';
import { LayoutGeneralComponent } from './layouts/layout-general/layout-general.component';
import { MyTeamsComponent } from './pages/team-admin/my-teams/my-teams.component';
import { LoginCallbackComponent } from './pages/general/login-callback/login-callback.component';
import { HttpRequestInterceptorService } from './services/http-request-interceptor.service';
import { HangTightComponent } from './pages/learner/results/hang-tight/hang-tight.component';
import { MyTeamModalComponent } from './pages/team-admin/my-teams/my-team-modal/my-team-modal.component';
import { MatTooltipModule } from '@angular/material/tooltip';
import { PortalModule } from '@angular/cdk/portal';
import { ClickedOutsideDirective } from './directives/clicked-outside.directive';
import { MatSnackBarModule } from '@angular/material/snack-bar';
import { LoadingSnakBarComponent } from './components/general/loading-states/loading-snak-bar/loading-snak-bar.component';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';

import { MatTabsModule } from '@angular/material/tabs';
import { MatBadgeModule } from '@angular/material/badge';
import { ResultsSkillsComponent } from './pages/learner/results/results-skills/results-skills.component';
import { SupportModalComponent } from './components/modals/support-modal/support-modal.component';
import { InlineSVGModule } from 'ng-inline-svg-2';
import { ButtonComponent } from './components/shared/button/button.component';
import { ModalComponent } from './components/shared/modal/modal.component';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { TooltipComponent } from './components/shared/tooltip/tooltip.component';
import { ResultsMessageAdherenceComponent } from './pages/learner/results/results-message-adherence/results-message-adherence.component';
import { LearnerDashboardComponent } from './pages/learner/learner-dashboard/learner-dashboard.component';
import { LearnerDashboardTabContentComponent } from './pages/learner/learner-dashboard/learner-dashboard-tab-content/learner-dashboard-tab-content.component';
import { AssignmentCardComponent } from './pages/learner/learner-dashboard/assignment-card/assignment-card';
import { CustomerAccountListModalComponent } from './components/modals/customer-account-list-modal/customer-account-list-modal.component';
import { ThreeDotsBlinkingVerticalComponent } from './components/general/loading-states/three-dots-blinking-vertical/three-dots-blinking-vertical.component';
import { CustomTooltipComponent } from './components/shared/custom-tooltip/custom-tooltip.component';
import { NewMessageAdherenceComponent } from './pages/learner/results/results-conversational-new/new-message-adherence/new-message-adherence.component';
import { NewObjectivesComponent } from './pages/learner/results/results-conversational-new/new-objectives/new-objectives.component';
import { FormsModule } from '@angular/forms';
import { ResultsTranscriptComponent } from './pages/learner/results/results-transcript/results-transcript.component'; // Import FormsModule
import { ResultsModule } from 'src/app/pages/learner/results/results.module';
import { ResultsConversationalComponentNew } from './pages/learner/results/results-conversational-new/results-conversational-new.component';
import { ResultsSkillsComponentNew } from './pages/learner/results/results-skills-new/results-skills-new.component';
import { BoldExtractorPipe } from './pipes/bold-extractor.pipe';
import { SbNavbarComponent } from './components/layouts/navbar/sb-navbar/sb-navbar.component';
import { qaiUiImports } from './qai-ui-imports';
import { ResultsAccuracyComponent } from './pages/learner/results/results-conversational-new/results-accuracy/results-accuracy.component';
import { MessageBlockComponent } from './components/shared/message-block/message-block.component';
import { QaiToastComponent, QaiToastService } from '@qai/ui';
import { AssignmentModule } from './pages/learner/assignment/assignment.module';
import { SharedComponentsModule } from './components/shared-components.module';
import { CallModule } from './pages/learner/call/call.module';
import { TranslocoRootModule } from './transloco-root.module';
import { TranslocoModule, TranslocoService } from '@jsverse/transloco';
import { LearningPathwayComponent } from './pages/learner/learning-pathway/learning-pathway.component';
import { ImageViewerComponent } from './pages/general/image-viewer/image-viewer.component';
import { ScormComponent } from './pages/general/scorm/scorm.component';
import { LOCALE_ID } from '@angular/core';
import { LanguageService } from './services/language.service';
import { registerAllLocales } from './utils/locale-registration.util';
import { LocaleService } from './services/locale.service';
import { PipesModule } from './pipes/pipes.module';
import { DemoModeInterceptor } from './services/demo-mode/http-request-demo-mode-interceptor.service';
import { PathwayDetailHeaderComponent } from './pages/learner/learning-pathway/detail-header/pathway-detail-header.component';
import { PathwayDetailsComponent } from "./pages/learner/learning-pathway/pathway-details/pathway-details.component";
import { SimulationGenerationBannerHostComponent } from "./pages/self-serve/simulation-builder/components/simulation-generation-banner-host/simulation-generation-banner-host.component";
import { IosBottomNavComponent } from './components/layouts/ios-bottom-nav/ios-bottom-nav.component';
import { IosAccountComponent } from './pages/learner/ios-account/ios-account.component';
import { CallRefinementBannerHostComponent } from "./pages/self-serve/simulation-builder/components/call-refinement-banner-host/call-refinement-banner-host.component";

registerAllLocales();

export function setInitialLang(languageService: LanguageService) {
  return () => {
    languageService.initializeLanguage();
  };
}
@NgModule({ declarations: [
        AppComponent,
        LoginComponent,
        HomeComponent,
        ResultsConversationalComponentNew,
        TeamAdminHomeComponent,
        ErrorDialogComponent,
        NavbarComponent,
        LayoutGeneralComponent,
        MyTeamsComponent,
        LoginCallbackComponent,
        MyTeamModalComponent,
        ClickedOutsideDirective,
        LoadingSnakBarComponent,
        ResultsSkillsComponentNew,
        SupportModalComponent,
        ResultsMessageAdherenceComponent,
        LearnerDashboardComponent,
        LearnerDashboardTabContentComponent,
        AssignmentCardComponent,
        CustomerAccountListModalComponent,
        ThreeDotsBlinkingVerticalComponent,
        CustomTooltipComponent,
        NewMessageAdherenceComponent,
        NewObjectivesComponent,
        BoldExtractorPipe,
        SbNavbarComponent,
        LearningPathwayComponent,
        ImageViewerComponent,
        PathwayDetailHeaderComponent,
        ScormComponent,
        IosBottomNavComponent,
        IosAccountComponent,
    ],
    bootstrap: [AppComponent], imports: [AppRoutingModule,
    ResultsModule,
    BrowserModule,
    BrowserAnimationsModule,
    MatIconModule,
    MatFormFieldModule,
    PortalModule,
    ReactiveFormsModule,
    MatSelectModule,
    MatMenuModule,
    MatDialogModule,
    MatTooltipModule,
    MatSnackBarModule,
    MatProgressSpinnerModule,
    MatTabsModule,
    MatBadgeModule,
    InlineSVGModule.forRoot(),
    ButtonComponent,
    ModalComponent,
    MatProgressBarModule,
    TooltipComponent,
    FormsModule,
    PipesModule,
    SharedComponentsModule,
    AssignmentModule,
    qaiUiImports,
    CallModule,
    TranslocoRootModule,
    TranslocoModule,
    PathwayDetailsComponent,
    QaiToastComponent,
    SimulationGenerationBannerHostComponent,
    CallRefinementBannerHostComponent,
    ResultsAccuracyComponent,
    MessageBlockComponent], providers: [
        QaiToastService,
        {
            provide: ErrorHandler,
            useValue: Sentry.createErrorHandler({
                showDialog: false,
            }),
        },
        {
            provide: Sentry.TraceService,
            deps: [Router],
        },
        {
            provide: APP_INITIALIZER,
            useFactory: (traceService: Sentry.TraceService) => () => {
                // Initialize Sentry TraceService
                return Promise.resolve();
            },
            deps: [Sentry.TraceService],
            multi: true,
        },
        {
            provide: APP_INITIALIZER,
            useFactory: setInitialLang,
            deps: [LanguageService],
            multi: true,
        },
        {
            provide: HTTP_INTERCEPTORS,
            useClass: DemoModeInterceptor,
            multi: true,
        },
        {
            provide: HTTP_INTERCEPTORS,
            useClass: HttpRequestInterceptorService,
            multi: true,
        },
        {
            provide: LOCALE_ID,
            useFactory: (localeService: LocaleService) => {
                return localeService.getCurrentLocaleId();
            },
            deps: [LocaleService],
        },
        provideHttpClient(withInterceptorsFromDi()),
    ] })
export class AppModule {}
